/* eslint-disable react/react-in-jsx-scope */
import { Skeleton } from "@/components/ui/skeleton";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import * as turf from "@turf/turf";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./MapPrograms.css";

const normalizeToLngLat = (coord: unknown): [number, number] | null => {
  if (!Array.isArray(coord) || coord.length < 2) return null;

  const first = Number(coord[0]);
  const second = Number(coord[1]);

  if (!Number.isFinite(first) || !Number.isFinite(second)) return null;

  const firstLooksLikeLng = Math.abs(first) <= 180 && Math.abs(second) <= 90;
  const firstLooksLikeLat = Math.abs(first) <= 90 && Math.abs(second) <= 180;

  if (firstLooksLikeLng && !firstLooksLikeLat) {
    return [first, second];
  }

  if (firstLooksLikeLat) {
    return [second, first];
  }

  return [first, second];
};

const MapClusterPrograms = ({
  projectsSelected,
  idProgram,
}: {
  projectsSelected?: any;
  idProgram?: number | null;
}) => {
  const url = `${import.meta.env.VITE_API_ENDPOINT}/folders/all${idProgram ? `?program=${idProgram}` : ""
    }`;
  const { id, firstname } = useParams<{ id: string; firstname: string }>();
  const folders = useFetchDataV2(url, `folders-${id}`, 1000 * 60 * 60);

  const mapContainerRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const hasZoomedRef = useRef(false);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const navigate = useNavigate();
  useEffect(() => {
    // On attend que le skeleton disparaisse pour être sûr que le div ref existe
    if (!folders.loading && mapContainerRef.current && !mapInstance) {
      const map = new maplibregl.Map({
        container: mapContainerRef.current,
        style:
          "https://tiles.stadiamaps.com/styles/alidade_smooth.json?api_key=" +
          import.meta.env.VITE_API_KEY_STADIAMAP,
        center: [0, 0],
        zoom: 2,
      });

      map.addControl(new maplibregl.NavigationControl());

      map.on("load", () => {
        try {
          map.setPaintProperty("water", "fill-color", "#75CFF0");
          map.setPaintProperty("background", "background-color", "#E7EBCC");
          map.setPaintProperty("building", "fill-color", "#E7EBCC");
        } catch { }
      });

      setMapInstance(map);
    }
    // 🔴 pas de setMapInstance(null) ici : on ne veut pas reset la map à chaque rerender
  }, [folders.loading]); // ← on déclenche quand le skeleton disparaît
  useEffect(() => {
    if (!mapInstance || !folders.data) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    const ids = Array.isArray(projectsSelected)
      ? projectsSelected.map((p) => p.id)
      : [];
    const selectedIds =
      Array.isArray(ids) && ids.length > 0
        ? new Set<number | string>(ids as (number | string)[])
        : null;
    console.log("selectedIds", selectedIds);
    const markerCollection = {
      type: "FeatureCollection",
      features: [],
    };

    // Affiche les marqueurs
    folders?.data?.forEach((row) => {
      // ✅ NEW: si on a une sélection et que l'id projet ne matche pas, on skip
      if (selectedIds && !selectedIds.has(row?.project?.id)) {
        return;
      }

      // ⚠️ On reconstitue les variables attendues par l'ancien code
      const program = row.program || { name: "-" };
      const project = row.project || { name: "-" };

      const allCoordinates: [number, number][] = [];

      // Nouveau format : row.locations[].coordinates déjà en [lng, lat] (on ne touche pas ta logique existante)
      if (row.locations) {
        row.locations.forEach((loc) => {
          (loc.coordinates || []).forEach((coord) => {
            const normalized = normalizeToLngLat(coord);
            if (normalized) allCoordinates.push(normalized);
          });
        });
      }

      // Nouveau format : row.parcels (si présent) -> même logique, on ignore les styles
      if (row.parcels) {
        row.parcels.forEach((par) => {
          (par.coordinates || []).forEach((coord) => {
            const normalized = normalizeToLngLat(coord);
            if (normalized) allCoordinates.push(normalized);
          });
        });
      }

      if (allCoordinates.length > 0) {
        const points = turf.featureCollection(
          allCoordinates.map((coord) =>
            turf.point(coord, { projectName: project.name })
          )
        );

        const clusters: any[] = [];
        points.features.forEach((point) => {
          let added = false;
          for (const cluster of clusters) {
            const dist = turf.distance(
              turf.center(turf.featureCollection(cluster)),
              point,
              { units: "kilometers" }
            );
            if (dist <= 100) {
              cluster.push(point);
              added = true;
              break;
            }
          }
          if (!added) {
            clusters.push([point]);
          }
        });

        clusters.forEach((cluster) => {
          const clusterCenter = turf.center(turf.featureCollection(cluster));
          markerCollection.features.push({
            type: "Feature",
            geometry: clusterCenter.geometry,
            properties: {
              html: `
                  <div class="map-cluster-popup">
                    <img 
                      src="${import.meta.env.VITE_MEDIA_BASE + "/media/" + project?.defaultImage?.uri}"
                      class="map-cluster-popup__image"
                    />
                    <div class="map-cluster-popup__body">
                      <p><b>PP :</b> ${program?.carrier}</p>
                      <p><b>Programme :</b> ${program?.name}</p>
                      <p><b>Projet :</b> ${project?.name}</p>
                      <p><b>Type :</b> ${project?.type}</p>
                      <div class="map-cluster-popup__actions">
                        <button
                          onclick="window.location.href='/${firstname}/project/${project.id}'"
                          class="map-cluster-popup__button"
                        >
                          Voir le projet
                        </button>
                      </div>
                    </div>
                  </div>
                `,
              coordinates: cluster.map((c: any) => c.geometry.coordinates),
              project,
              program,
              polygons: Array.isArray(row.locations)
                ? row.locations
                  .map((loc) =>
                    Array.isArray(loc.coordinates) ? loc.coordinates : null
                  )
                  .filter(Boolean)
                : [],
            },
          });
        });
      }
    });

    markerCollection.features.forEach((feature) => {
      const el = document.createElement("div");
      el.className = "marker";
      el.style.width = "25px";
      el.style.height = "25px";
      el.style.borderRadius = "50%";

      const marker = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat(feature.geometry.coordinates)
        .setPopup(
          new maplibregl.Popup({ offset: 25 }).setHTML(
            feature.properties.html
          )
        )
        .addTo(mapInstance);
      markersRef.current.push(marker);

      marker.getElement().addEventListener("click", () => {
        displayClusterPolygons(feature, mapInstance);
      });
    });

    // Fit bounds une fois
    const bounds = new maplibregl.LngLatBounds();
    markerCollection.features.forEach((feature) => {
      bounds.extend(feature.geometry.coordinates);
    });

    if (!hasZoomedRef.current && !bounds.isEmpty()) {
      mapInstance.fitBounds(bounds, {
        padding: {
          top: 90,
          right: 90,
          bottom: 90,
          left: 90,
        },
        duration: 1000,
      });
      hasZoomedRef.current = true;
    }

    console.log("rendu marker");
    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      hasZoomedRef.current = false;
    };
  }, [mapInstance, folders.data, projectsSelected]);

  const displayClusterPolygons = (feature, map) => {
    // Supprimer l'ancien polygone s'il existe
    if (map.getSource("polygon-source")) {
      map.removeLayer("polygon-layer");
      map.removeSource("polygon-source");
    }
    const polygons = feature?.properties?.polygons;
    console.log("feature", feature);
    if (!Array.isArray(polygons) || polygons.length === 0) {
      console.error(
        "Aucune donnée de polygone trouvée dans feature.properties.polygons !"
      );
      return;
    }

    // Construire les polygones avec des boucles fermées
    const geojsonFeatures = polygons
      .map((coords) => {
        if (!Array.isArray(coords) || coords.length === 0) return null; // Vérification des données

        const polygonCoords = coords.map((coord) => {
          if (coord.length === 2) {
            return [coord[1], coord[0]]; // Inversion lat/lng -> [lng, lat]
          }
          return coord;
        });
        if (
          polygonCoords[0].toString() !==
          polygonCoords[polygonCoords.length - 1].toString()
        ) {
          polygonCoords.push([...polygonCoords[0]]); // Fermer le polygone
        }

        return {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [polygonCoords], // Chaque ensemble de coordonnées forme un polygone fermé
          },
        };
      })
      .filter(Boolean); // Filtrer les valeurs nulles

    if (geojsonFeatures.length === 0) {
      console.error("Aucune donnée valide pour le polygone !");
      return;
    }

    // Ajouter la source GeoJSON
    map.addSource("polygon-source", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: geojsonFeatures,
      },
    });

    // Ajouter un calque pour afficher les polygones
    map.addLayer({
      id: "polygon-layer",
      type: "fill",
      source: "polygon-source",
      paint: {
        "fill-color": "rgba(0, 0, 255, 0.5)", // Couleur bleue avec transparence
        "fill-outline-color": "rgba(0, 0, 255, 0.5)", // Bordure bleue
      },
    });

    // Construire le polygone en fermant la boucle (le dernier point doit être le même que le premier)
    const polygonCoordinates = [...feature.properties.coordinates];
    if (
      polygonCoordinates[0].toString() !==
      polygonCoordinates[polygonCoordinates.length - 1].toString()
    ) {
      polygonCoordinates.push(polygonCoordinates[0]);
    }
    // Définir les limites pour bien cadrer le polygone
    const bounds = new maplibregl.LngLatBounds();
    polygonCoordinates.forEach((coord) => bounds.extend(coord));

    // Vérifier que les limites ne sont pas vides avant d'ajuster la vue
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: {
          top: 90,
          right: 90,
          bottom: 90,
          left: 90,
        },
        duration: 1000, // Animation douce
      });
    }
  };

  if (folders.loading) {
    return <MapSkeleton />;
  }
  return (
    <>
      <div
        ref={mapContainerRef}
        style={{ height: "82vh", width: "100%", zIndex: "1" }}
      />
    </>
  );
};
const MapSkeleton = () => {
  return (
    <div>
      <Skeleton className=" w-[500px] h-[600px] rounded-lg bg-[#ecedee]" />
    </div>
  );
};
export default MapClusterPrograms;
